import type {
  MeioCitacaoRequerido,
  MotivoEncerramentoFisicoCitacao,
  OpcaoStatusCitacaoRequerido,
  StatusCitacaoRequerido,
} from '@/types/arbitral/citacaoRequerido';

import { capitalizarPrimeiraLetra, rotularEnum } from './text';

/**
 * Nome TÉCNICO de cada status da citação, na ordem do ciclo: é o que o editor do fluxo oferece no
 * bloco `arbitral.update_status_citacao`, onde cada status se grava pelo nome. As telas não mostram
 * o status — mostram a SITUAÇÃO da citação (`situacaoDaCitacao.ts`, D-04), em que seis status são
 * "Citado". O `Record` obriga a dar nome a todo status novo do union.
 */
const ROTULO_DO_STATUS_CITACAO: Record<StatusCitacaoRequerido, string> = {
  NAO_ENVIADO: 'Não enviado',
  ENVIANDO: 'Enviando',
  ENVIADO: 'Enviado',
  VISUALIZADO: 'Visualizado',
  RESPONDIDO: 'Respondido',
  CIENCIA_CONFIRMADA: 'Ciência confirmada',
  CIENCIA_COM_DUVIDAS: 'Ciência com dúvidas',
  ENTREGUE_SEM_CIENCIA: 'Entregue sem ciência',
  IDENTIDADE_REJEITADA: 'Identidade rejeitada',
  // O digital esgotou sem ciência e a citação segue pela carta com AR, ainda por fazer. "Encerrada
  // física" se lia como a citação física já concluída, o contrário do que o status diz.
  ENCERRADA_FISICA: 'Aguardando AR',
  BLOQUEADA_CONTATO: 'Bloqueada contato',
  BLOQUEADA_FALHA_DIGITAL: 'Bloqueada falha digital',
  MANIFESTACAO_PROTOCOLADA: 'Manifestação protocolada',
  // Citação comprovada pela entrega, sem leitura (CredAluga): conta como citado sem afirmar
  // "visualizado".
  CITACAO_POR_ENTREGA: 'Entregue (citação por entrega)',
};

/** Status da citação na ordem do ciclo: o dropdown do nó `arbitral.update_status_citacao`. */
export const STATUS_CITACAO_REQUERIDO_OPCOES: OpcaoStatusCitacaoRequerido[] = (
  Object.keys(ROTULO_DO_STATUS_CITACAO) as StatusCitacaoRequerido[]
).map((value) => ({ value, label: ROTULO_DO_STATUS_CITACAO[value] }));

/**
 * Espelho de `CITACAO_CONFIRMADA` (`citacaoRequeridoStatusSets.ts` do core-api): os status que
 * confirmam a citação. No editor, só com eles o nó grava o meio e libera o `aguardar_citacao`.
 */
export const STATUS_QUE_CONFIRMAM_CITACAO = [
  'VISUALIZADO',
  'RESPONDIDO',
  'CIENCIA_CONFIRMADA',
  'CIENCIA_COM_DUVIDAS',
  'MANIFESTACAO_PROTOCOLADA',
  'CITACAO_POR_ENTREGA',
] as const satisfies readonly StatusCitacaoRequerido[];

const ROTULO_DO_MOTIVO_ENCERRAMENTO_FISICO: Record<MotivoEncerramentoFisicoCitacao, string> = {
  NAO_SOU_EU: 'Não sou eu',
  RESPONDEU_SEM_CONFIRMAR: 'Respondeu sem confirmar',
  NUMERO_INVALIDO: 'Número inválido ou bloqueado',
  MENSAGENS_NAO_CHEGAM: 'Mensagens não chegam',
  INCONCLUSIVO: 'Sem retorno de entrega',
  RCS_FALHOU: 'RCS falhou',
  RCS_INDETERMINADO: 'RCS sem confirmação do provedor',
  RCS_INDISPONIVEL: 'RCS indisponível',
  SEM_CITACAO_APOS_RCS: 'Sem citação 24 h após o RCS',
  ENCERRADA_PELA_LEXI: 'Encerrada pela Lexi (120 h)',
};

/** Como cada meio da citação aparece para a Secretaria e no certificado. */
const ROTULO_DO_MEIO_CITACAO: Record<MeioCitacaoRequerido, string> = {
  AR: 'AR (Correios)',
  CLAUSULA_CONTRATUAL: 'Cláusula contratual',
  WHATSAPP: 'WhatsApp',
  EMAIL: 'E-mail',
  RCS: 'RCS',
};

/**
 * Valor que o front ainda não conhece vira o enum humanizado ("STATUS_NOVO" → "Status novo"), e
 * não um rótulo genérico: a Secretaria precisa distinguir um do outro até o deploy do front.
 */
function rotuloDeValorDesconhecido(valor: string): string {
  return capitalizarPrimeiraLetra(rotularEnum(valor));
}

/**
 * Para quem lê o motivo de um JSON livre (o `contexto` do alerta): só valor do enum é motivo — texto
 * escrito à mão num fluxo antigo não pode virar rótulo de motivo.
 */
export function ehMotivoEncerramentoFisicoCitacao(
  motivo: string,
): motivo is MotivoEncerramentoFisicoCitacao {
  return Object.hasOwn(ROTULO_DO_MOTIVO_ENCERRAMENTO_FISICO, motivo);
}

function ehStatusCitacaoConhecido(status: string): status is StatusCitacaoRequerido {
  return Object.hasOwn(ROTULO_DO_STATUS_CITACAO, status);
}

/**
 * O nome técnico do status. Fora do editor, só para o core anterior à D-04, que não manda a
 * situação: TODO(#1437) — sai das telas com esse fallback.
 *
 * @example rotuloStatusCitacao('ENCERRADA_FISICA') // 'Aguardando AR'
 */
export function rotuloStatusCitacao(status: string): string {
  return ehStatusCitacaoConhecido(status)
    ? ROTULO_DO_STATUS_CITACAO[status]
    : rotuloDeValorDesconhecido(status);
}

function ehMeioCitacaoConhecido(meio: string): meio is MeioCitacaoRequerido {
  return Object.hasOwn(ROTULO_DO_MEIO_CITACAO, meio);
}

/** @example rotuloMeioCitacao('CLAUSULA_CONTRATUAL') // 'Cláusula contratual' */
export function rotuloMeioCitacao(meio: string): string {
  return ehMeioCitacaoConhecido(meio)
    ? ROTULO_DO_MEIO_CITACAO[meio]
    : rotuloDeValorDesconhecido(meio);
}

/** @example rotuloMotivoEncerramentoFisico('INCONCLUSIVO') // 'Sem retorno de entrega' */
export function rotuloMotivoEncerramentoFisico(motivo: string): string {
  return ehMotivoEncerramentoFisicoCitacao(motivo)
    ? ROTULO_DO_MOTIVO_ENCERRAMENTO_FISICO[motivo]
    : rotuloDeValorDesconhecido(motivo);
}
